import { useState, useEffect, useContext } from 'react';
import { Box, Button, TextField, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import axios from 'utils/axios';
import JWTContext from 'contexts/JWTContext';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const TwoFactorSetupForm = ({ onSuccess, showCancel, onCancel }: {
  onSuccess?: () => void;
  showCancel?: boolean;
  onCancel?: () => void;
}) => {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const jwtContext = useContext(JWTContext);

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema: Yup.object({
      code: Yup.string()
        .required('Verification code is required')
        .matches(/^\d{6}$/, 'Code must be 6 digits')
    }),
    onSubmit: async (values) => {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('temp_token');
      try {
        await axios.post('/berryAPI/2fa/verifySetup', { code: values.code, secret }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Two Factor Authentication has been enabled successfully');
        setShowConfirmation(true);
        if (onSuccess) onSuccess();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Invalid verification code');
      }
    }
  });

  useEffect(() => {
    const fetchSetup = async () => {
      const token = localStorage.getItem('temp_token'); 
      try {
        const response = await axios.get('/berryAPI/2fa/enable', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQrCode(response.data.qrCodeImage);
        setSecret(response.data.secret);
      } catch {
        setError('Failed to generate Two Factor Authentication setup');
      }
    };
    fetchSetup();
  }, []);

  const handleLogout = async () => {
    var loginRedirect = null;
    if (window.location.href.search("redir=") >= 0)
    {
      loginRedirect = unescape(window.location.href.split("redir=")[1].split("&")[0]);
    }
    
    jwtContext.logout(loginRedirect);
  };

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Typography variant="body2" paragraph>
        1. Download an authenticator app on your phone (e.g., Microsoft Authenticator, Google Authenticator, or Authy):
      </Typography>
      <Typography variant="body2" paragraph>
        2. Open the authenticator app and tap the "+" or "Add Account" button
      </Typography>
      <Typography variant="body2" paragraph>
        3. Choose "Scan QR Code" and point your phone's camera at this code:
      </Typography>
      {qrCode && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <img
            src={`data:image/png;base64,${qrCode}`}
            alt="QR Code"
            style={{ maxWidth: '200px' }}
          />
        </Box>
      )}
      <Typography variant="body2" paragraph>
        4. If you can't scan the QR code, you can enter this code manually in your authenticator app:
      </Typography>
      {secret && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'monospace',
              letterSpacing: 1,
              wordBreak: 'break-all',
              mt: 1,
              mb: 1,
              display: 'inline-block',
              px: 2,
              py: 1,
              background: '#f5f5f5',
              borderRadius: 1,
            }}
          >
            {secret}
          </Typography>
        </Box>
      )}
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'bold',
          color: 'text.secondary',
          mb: 2,
          fontSize: '10px'
        }}
      >
        Note: To allow multiple users to access this account, share this QR code or secret key with them. 
        You can access this QR code again later in the Two Factor Authentication settings page.
      </Typography>
      <Typography variant="body2" paragraph>
        5. Once the code is added, your authenticator app will show a 6-digit number that changes every 30 seconds
      </Typography>
      <Typography variant="body2" paragraph>
        6. Enter the current 6-digit number from your authenticator app below:
      </Typography>
      <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
        <TextField
          fullWidth
          margin="normal"
          name="code"
          label="Verification Code"
          value={formik.values.code}
          onChange={formik.handleChange}
          error={formik.touched.code && Boolean(formik.errors.code)}
          helperText={formik.touched.code && formik.errors.code}
          inputProps={{ maxLength: 6 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          {showCancel && <Button onClick={onCancel} variant="outlined">Cancel</Button>}
          <Button type="submit" variant="contained">Verify</Button>
        </Box>
      </Box>
      <Dialog open={showConfirmation} onClose={() => {}}>
        <DialogTitle>Two-Factor Authentication Enabled</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Two-factor authentication has been successfully enabled for your account.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            For security reasons, you will need to sign in again with your new Two Factor Authentication settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout} color="primary" variant="contained">
            Sign Out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TwoFactorSetupForm; 